import { motion } from 'motion/react';
import { usePendingQuizzes } from '@/hooks/usePendingQuizzes';
import { PendingQuizRow } from './PendingQuizRow';
import styles from './ReviewCenter.module.scss';

export function ReviewCenter() {
  const { pendingQuizzes, counts, isLoading, isError, refetch } = usePendingQuizzes();

  if (isLoading) {
    return (
      <div className={styles.reviewCenter}>
        <div className={styles.reviewCenter__container}>
          <div className={styles.reviewCenter__loading}>
            <div className={styles.reviewCenter__spinner} />
            <p>Loading pending quizzes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.reviewCenter}>
        <div className={styles.reviewCenter__container}>
          <div className={styles.reviewCenter__error}>
            <p>Failed to load pending quizzes</p>
            <button onClick={() => refetch()}>Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reviewCenter}>
      <div className={styles.reviewCenter__container}>
        <motion.header
          className={styles.reviewCenter__header}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1 className={styles.reviewCenter__title}>Knowledge Deck Review Center</h1>
          <p className={styles.reviewCenter__subtitle}>
            Review and save your generated quizzes before adding them to your collection
          </p>
          <div className={styles.reviewCenter__stats}>
            <span className={styles.reviewCenter__stat}>
              <strong>{counts.total}</strong> pending
            </span>
            <span className={`${styles.reviewCenter__stat} ${styles['reviewCenter__stat--ready']}`}>
              <strong>{counts.ready}</strong> ready
            </span>
            {counts.processing > 0 && (
              <span className={`${styles.reviewCenter__stat} ${styles['reviewCenter__stat--processing']}`}>
                <strong>{counts.processing}</strong> generating
              </span>
            )}
            {counts.failed > 0 && (
              <span className={`${styles.reviewCenter__stat} ${styles['reviewCenter__stat--failed']}`}>
                <strong>{counts.failed}</strong> failed
              </span>
            )}
          </div>
        </motion.header>

        <div className={styles.reviewCenter__content}>
          {pendingQuizzes.length === 0 ? (
            <motion.div
              className={styles.reviewCenter__empty}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <div className={styles.reviewCenter__emptyIcon}>
                <EmptyIcon />
              </div>
              <h2>No Pending Quizzes</h2>
              <p>
                When you generate a quiz, it will appear here for you to review before saving.
              </p>
            </motion.div>
          ) : (
            <motion.div
              className={styles.reviewCenter__list}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {pendingQuizzes.map((quiz, index) => (
                <PendingQuizRow
                  key={quiz.id}
                  quiz={quiz}
                  index={index}
                />
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="12" width="48" height="40" rx="4" stroke="currentColor" strokeWidth="2" />
      <path d="M8 24H56" stroke="currentColor" strokeWidth="2" />
      <rect x="16" y="32" width="32" height="4" rx="2" fill="currentColor" opacity="0.3" />
      <rect x="16" y="40" width="24" height="4" rx="2" fill="currentColor" opacity="0.3" />
    </svg>
  );
}
